import { ImageLoader } from "~/components/ImageLoader";
import { BlogPost, getPostImage, urlFor } from "../../api/sanity";
import { CategoryBadge } from "~/modules/blog/components/CategoryBadge";
import { AuthorByline } from "~/modules/blog/components/PostMeta";
import { PortableText } from "@portabletext/react";

// // // //

// image-<hash>-<width>x<height>-<ext>  →  natural dimensions
const getImageDimensions = (value: any) => {
    const ref: string | undefined = value?.asset?._ref;
    const match = ref?.match(/-(\d+)x(\d+)-/);
    if (!match) return { width: 1200, height: 675 };
    const w = parseInt(match[1], 10);
    const h = parseInt(match[2], 10);
    const width = Math.min(w, 1200);
    return { width, height: Math.round((width * h) / w) };
};

// Portable Text components for rich text rendering
const portableTextComponents = {
    block: {
        normal: ({ children }: any) => (
            <p className="mb-6 text-base leading-relaxed font-light text-foreground">
                {children}
            </p>
        ),
        h1: ({ children }: any) => (
            <h1 className="mb-6 mt-8 text-4xl font-light text-foreground">
                {children}
            </h1>
        ),
        h2: ({ children }: any) => (
            <h2 className="mb-4 mt-8 text-3xl font-light text-foreground">
                {children}
            </h2>
        ),
        h3: ({ children }: any) => (
            <h3 className="mb-4 mt-6 text-3xl font-light text-foreground">
                {children}
            </h3>
        ),
        h4: ({ children }: any) => (
            <h4 className="mb-3 mt-6 text-3xl font-light text-foreground">
                {children}
            </h4>
        ),
        h5: ({ children }: any) => (
            <h5 className="mb-3 mt-6 text-3xl font-light text-foreground">
                {children}
            </h5>
        ),
        h6: ({ children }: any) => (
            <h6 className="mb-2 mt-4 text-2xl font-light text-foreground">
                {children}
            </h6>
        ),
        blockquote: ({ children }: any) => (
            <blockquote className="my-6 border p-6 italic rounded-lg leading-8 font-light text-base bg-blue-50 border-blue-500/50">
                {children}
            </blockquote>
        ),
    },
    list: {
        // list-outside + padding so long, multi-sentence items wrap cleanly
        bullet: ({ children }: any) => (
            <ul className="mb-6 list-outside list-disc space-y-2 pl-6 text-base text-foreground">
                {children}
            </ul>
        ),
        number: ({ children }: any) => (
            <ol className="mb-6 list-outside list-decimal space-y-2 pl-6 text-base text-foreground">
                {children}
            </ol>
        ),
    },
    listItem: {
        bullet: ({ children }: any) => (
            <li className="pl-1 leading-relaxed">{children}</li>
        ),
        number: ({ children }: any) => (
            <li className="pl-1 leading-relaxed">{children}</li>
        ),
    },
    marks: {
        strong: ({ children }: any) => (
            <strong className="font-semibold">{children}</strong>
        ),
        em: ({ children }: any) => <em className="italic">{children}</em>,
        underline: ({ children }: any) => (
            <span className="underline">{children}</span>
        ),
        "strike-through": ({ children }: any) => (
            <s className="line-through">{children}</s>
        ),
        code: ({ children }: any) => (
            <code className="rounded bg-muted px-2 py-1 font-mono text-sm">
                {children}
            </code>
        ),
        link: ({ children, value }: any) => {
            const href: string = value?.href || "#";
            const isExternal = /^https?:\/\//i.test(href);
            // respect the schema's openInNewTab; default external links to a new tab,
            // but never force it on mailto:/tel:/relative links
            const openInNewTab = value?.openInNewTab ?? isExternal;
            return (
                <a
                    href={href}
                    {...(openInNewTab
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                    className="text-link underline hover:text-link-hover"
                >
                    {children}
                </a>
            );
        },
    },
    types: {
        image: ({ value }: any) => {
            // empty placeholder blocks (no asset) render nothing
            if (!value?.asset) return null;
            const { width, height } = getImageDimensions(value);
            return (
                <figure className="my-8">
                    <ImageLoader
                        src={urlFor(value)
                            .width(width)
                            .fit("max")
                            .auto("format")
                            .url()}
                        alt={value.alt || "Blog post image"}
                        width={width}
                        height={height}
                        className="mx-auto h-auto w-full"
                    />
                    {value.caption && (
                        <figcaption className="mt-2 text-center text-sm italic text-muted-foreground">
                            {value.caption}
                        </figcaption>
                    )}
                </figure>
            );
        },
    },
};

export function BlogIndividualPage(props: { post: BlogPost }) {
    const { post } = props;

    const postImage = getPostImage(post);

    return (
        <article className="mx-auto max-w-3xl py-10">
            {/* Header */}
            <header className="mb-8">
                <CategoryBadge post={post} className="mb-4" />
                <h1 className="mb-6 text-4xl leading-tight text-foreground">
                    {post.title}
                </h1>

                {/* Excerpt */}
                {post.excerpt && (
                    <p className="mb-6 text-base leading-8 font-light">
                        {post.excerpt}
                    </p>
                )}

                <AuthorByline post={post} />
            </header>

            {/* Featured Image */}
            {postImage && (
                <div className="mb-10 overflow-hidden rounded-lg flex justify-center">
                    <ImageLoader
                        src={urlFor(postImage).url()}
                        alt={post.title}
                        className="w-full"
                    />
                </div>
            )}

            <div className="h-px w-full bg-border mb-8" />

            {/* Post Content */}
            {post.body && (
                <div className="prose prose-lg max-w-none">
                    <PortableText
                        value={post.body}
                        components={portableTextComponents}
                    />
                </div>
            )}
        </article>
    );
}
